import React, {useEffect, useState} from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Collapse, Box as MuiBox,
    List, ListItemText, CircularProgress, Typography, ListItemButton, Button, Box, Stack
} from '@mui/material';
import {ExpandLess, ExpandMore, InfoOutlined} from '@mui/icons-material';
import {Dept, useDeptTreeList, useDeleteDept} from '@/services/manageService/deptService';
import DeptDetail from '@/pages/components/manage/sys/dept/DeptDetail';
import {useTheme} from '@mui/material/styles';
import {PopoverButton, SnackbarField} from '@/components/common';
import {usePageCrud} from '@/hooks/usePageCrud';

interface DeptNodeProps {
    node: Dept;
    level: number;
    onDetails: (id: number) => void;
    onRemove: (id: number) => void;
}

const DeptNode: React.FC<DeptNodeProps> = ({node, level, onDetails, onRemove}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const hasChildren = node.children && node.children.length > 0;

    return (
        <>
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    pl: 2 + level * 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'transparent' : 'background.default',
                    borderRadius: 1
                }}
            >
                <ListItemButton sx={{flex: 1}} onClick={() => setOpen((prev) => !prev)}>
                    {hasChildren ? (
                        open ? <ExpandLess fontSize="small"/> : <ExpandMore fontSize="small"/>
                    ) : null}
                    <ListItemText
                        primaryTypographyProps={{color: 'text.primary', fontWeight: 500}}
                        secondaryTypographyProps={{color: 'text.secondary'}}
                        primary={node.name}
                        secondary={node.companyName}
                    />
                </ListItemButton>
                <Stack direction="row" spacing={1} sx={{mr: 2}}>
                    <Button size="small" variant="outlined"
                            sx={{
                                minWidth: 80,
                                textTransform: 'none',
                                borderColor: theme.palette.secondary.main,
                                color: theme.palette.secondary.main,
                                fontWeight: 600,
                                '&:hover': {
                                    borderColor: theme.palette.secondary.dark,
                                    color: theme.palette.secondary.dark
                                }
                            }}
                            startIcon={<InfoOutlined/>} onClick={() => onDetails(node.deptId)}>
                        Details
                    </Button>
                    {String(node.name ?? '').toLowerCase() !== 'superadmin' && (
                    <PopoverButton nodeId={node.deptId} nodeName={node.name} onRemove={onRemove} theme={theme}/>
                    )}
                </Stack>
            </Box>
            {hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {node.children!.map((child) => (
                            <DeptNode key={child.deptId} node={child} level={level + 1} onDetails={onDetails}
                                      onRemove={onRemove}/>
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

const flattenDeptTree = (tree: Dept[], level = 0, parentPath: number[] = []): any[] => {
    let rows: any[] = [];
    tree.forEach((node) => {
        rows.push({...node, level, parentPath});
        if (node.children && node.children.length > 0) {
            rows = rows.concat(flattenDeptTree(node.children, level + 1, [...parentPath, node.deptId]));
        }
    });
    return rows;
};

const getVisibleRows = (rows: any[], openRowIds: number[]): any[] => {
    const result: any[] = [];
    const idSet = new Set(openRowIds);
    const idMap = new Map<number, any>();
    rows.forEach(r => idMap.set(r.deptId, r));
    const isParentClosed = (row: any) => row.parentPath?.some((pid: number) => !idSet.has(pid));
    for (const row of rows) {
        if (row.level === 0 || !isParentClosed(row)) {
            result.push(row);
        }
    }
    return result;
};

const DeptList: React.FC = () => {
    const theme = useTheme();
    const [tree, setTree] = useState<Dept[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openRowIds, setOpenRowIds] = useState<number[]>([]);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error'
    }>({open: false, message: '', severity: 'success'});

    // ========== 新增：统一 DeptDetail 弹窗控制 =============
    const [deptDetailOpen, setDeptDetailOpen] = useState(false);
    const [isAdd, setIsAdd] = useState(true);
    const [detailDeptId, setDetailDeptId] = useState<number | null>(null);
    const {canRead, canCreate, canRemove} = usePageCrud({
        read: ['admin_dept_query', 'admin_dept_detail', 'admin_dept_tree'],
        create: 'admin:dept:create',
        update: 'admin:dept:update',
        remove: 'admin:dept:delete',
    });

    useEffect(() => {
        setLoading(true);
        useDeptTreeList()
            .then((res) => {
                setTree(res || []);
                setError(null);
            })
            .catch((e) => {
                setError(e?.message || 'Load failed');
                setTree([]);
            })
            .finally(() => setLoading(false));
    }, []);

    const refreshList = () => {
        setLoading(true);
        useDeptTreeList()
            .then((res) => {
                setTree(res || []);
                setError(null);
            })
            .catch((e) => {
                setError(e?.message || 'Load failed');
                setTree([]);
            })
            .finally(() => setLoading(false));
    };

    // ========== 替换 handleDetails/handleAdd =============
    const handleDetails = (id: number) => {
        setDetailDeptId(id);
        setIsAdd(false);
        setDeptDetailOpen(true);
    };
    const handleAdd = () => {
        setIsAdd(true);
        setDetailDeptId(null);
        setDeptDetailOpen(true);
    };
    const handleDeptDetailClose = (data: any) => {
        if (data && data.message) {
            setSnackbar({
                open: true,
                message: data.message || (data.type === 'create' ? data.name +  ' Generated successful!' : data.name + ' Updated successful!'),
                severity: data.severity || 'success'
            });
        }
        setDeptDetailOpen(false);
        setDetailDeptId(null);
        if (data) refreshList();
    };

    const handleRemove = async (id: number) => {
        setRemoveLoading(true);
        try {
            await useDeleteDept(id);
            setSnackbar({open: true, message: 'Remove Successful!', severity: 'success'});
            refreshList();
        } catch (e: any) {
            const msg = e?.message || '删除失败';
            setSnackbar({open: true, message: msg, severity: 'error'});
        } finally {
            setRemoveLoading(false);
        }
    };

    const handleToggleRow = (id: number) => {
        setOpenRowIds((prev) => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    if (loading) return <CircularProgress sx={{display: 'block', mx: 'auto', my: 4}}/>;
    if (error) return <Typography color="error">{error}</Typography>;
    if (!tree || tree.length === 0) return <Typography>暂无部门数据</Typography>;

    const rows = flattenDeptTree(tree);
    const visibleRows = getVisibleRows(rows, openRowIds);

    return (
        <Box sx={{
            p: 2,
            bgcolor: 'transparent',
            height: 'calc(100vh - 120px)',
            minHeight: '70vh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                    Dept List
                </Typography>
                {canCreate && (
                <Button variant="contained" color="secondary" onClick={handleAdd}
                        sx={{
                            borderRadius: theme.shape.borderRadius,
                            textTransform: 'none',
                            fontWeight: 600
                        }}>ADD</Button>
                )}
            </Stack>
            <TableContainer
                component={Paper}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    boxShadow: theme.shadows[2],
                    bgcolor: 'background.paper',
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        minWidth: 700,
                        tableLayout: 'fixed',
                        '& .MuiTableCell-stickyHeader': {
                            bgcolor: 'background.paper',
                            zIndex: 3,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 56, p: 0 }}></TableCell>
                            <TableCell sx={{ width: 220, fontWeight: 700 }}>Company Name</TableCell>
                            <TableCell sx={{ width: 260, fontWeight: 700 }}>Dept Name</TableCell>
                            <TableCell sx={{ width: 120, fontWeight: 700 }} align="center">Current Num</TableCell>
                            <TableCell sx={{ width: 220, fontWeight: 700 }} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.map((row) => (
                            <React.Fragment key={row.deptId}>
                                <TableRow hover sx={{
                                    '& > *': {borderBottom: 'unset'},
                                    background: row.level % 2 === 0 ? theme.palette.background.default : 'transparent'
                                }}>
                                    <TableCell sx={{ width: 56 }}>
                                        {row.children && row.children.length > 0 && (
                                            <IconButton size="small" onClick={() => handleToggleRow(row.deptId)}>
                                                {openRowIds.includes(row.deptId) ? <ExpandLess/> : <ExpandMore/>}
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ width: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.companyName}</TableCell>
                                    <TableCell sx={{ width: 260, pl: 2 + row.level * 2, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</TableCell>
                                    <TableCell sx={{ width: 120 }} align="center">{row.currentNum}</TableCell>
                                    <TableCell sx={{ width: 220 }} align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            {canRead && (
                                            <Button size="small" variant="outlined"
                                                    sx={{
                                                        minWidth: 80,
                                                        textTransform: 'none',
                                                        borderColor: theme.palette.secondary.main,
                                                        color: theme.palette.secondary.main,
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            borderColor: theme.palette.secondary.dark,
                                                            color: theme.palette.secondary.dark
                                                        }
                                                    }}
                                                    startIcon={<InfoOutlined/>}
                                                    onClick={() => handleDetails(row.deptId)}>
                                                Details
                                            </Button>
                                            )}
                                            {canRemove && String(row.name ?? '').toLowerCase() !== 'superadmin' && (
                                            <PopoverButton nodeId={row.deptId} nodeName={row.name}
                                                           onRemove={handleRemove} theme={theme}/>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                                {row.children && row.children.length > 0 && (
                                    <TableRow>
                                        <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={5}>
                                            <Collapse in={openRowIds.includes(row.deptId)} timeout="auto" unmountOnExit>
                                                <MuiBox sx={{ bgcolor: 'background.default' }}>
                                                    {/* 子部门已在 rows 递归渲染，无需额外内容 */}
                                                </MuiBox>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {/* ========== 替换 DeptDetail 组件调用 ============= */}
            <DeptDetail
                open={deptDetailOpen}
                deptId={detailDeptId}
                isAdd={isAdd}
                onClose={handleDeptDetailClose}
            />
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                autoHideDuration={4000}
                onClose={() => setSnackbar({...snackbar, open: false})}
            />
        </Box>
    );
};

export default DeptList;
